document.addEventListener("DOMContentLoaded", () => {
  // =============================================
  // 1. STATE MANAGEMENT
  // =============================================
  const state = {
    editedBoard1: null,
    editedBoard2: null,
    notes: null,
    protection: "None",
    customizeBoard2: false,
    extraForBoard2: 0,
    price: 0,
    board1: {
      color: "None",
      image: null,
      imageUrl: null,
      text: "",
      font: "Anton",
      fontColor: "Black",
      imageTransform: null,
      textTransform: null,
    },
    board2: {
      color: "None",
      image: null,
      imageUrl: null,
      text: "",
      font: "Anton",
      fontColor: "Black",
      imageTransform: null,
      textTransform: null,
    },
    currentBoard: 1,
  };
  // Single instance of CornholeDesigner
  let designer = null;
  // =============================================
  // 2. PRICE VARIANTS
  // =============================================
  const PRICE_VARIANTS = {
    protection: [
      { value: "None", price: 0.0 },
      { value: "Yes", price: 50.0 },
      { value: "No", price: 0.0 },
    ],
  };
  const BASE_PRICE = 49.99;
  const EXTRA_BOARD2_PRICE = 10.0;

  const FORMAT_ICONS = {
    pdf: "https://cdn.shopify.com/s/files/1/0582/5400/0176/files/pdf-file.png",
    ai: "https://cdn.shopify.com/s/files/1/0582/5400/0176/files/ai-file.png",
    eps: "https://cdn.shopify.com/s/files/1/0582/5400/0176/files/eps-file.png",
    tiff: "https://cdn.shopify.com/s/files/1/0582/5400/0176/files/tif-file.png",
  };

  const NON_EDITABLE_EXTS = ["pdf", "ai", "eps", "tiff", "psd"];

  // Color mappings for background colors
  const COLOR_MAPPINGS = {
    None: "transparent",
    Black: "#000000",
    White: "#ffffff",
    Red: "#ff0003",
    "Ruby Red": "#9b111e",
    Orange: "#ff7b00",
    Yellow: "#fedc00",
    Green: "#00ff07",
    "Grass Green": "#008103",
    Blue: "#0022ff",
    "Sky Blue": "#00b7ff",
    Purple: "#8800ff",
    Magenta: "#ff00e1",
    Pink: "#ffa9cb",
  };
  const FONT_COLORS = {
    None: "transparent",
    Black: "#000000",
    White: "#ffffff",
    "Dark Gray": "#545454",
    Gray: "#737373",
    "Light Gray": "#d9d9d9",
    Red: "#ff0000",
    "Coral Red": "#ff5757",
    Pink: "#ff66c4",
    Magenta: "#cb6ce6",
    Purple: "#8c52ff",
    Violet: "#5e17eb",
    Turquoise: "#0097b2",
    "Light Blue": "#38b6ff",
    "Cobalt Blue": "#004aad",
    Navy: "#224092",
    Green: "#00bf63",
    "Grass Green": "#7ed957",
    Yellow: "#ffde59",
    Peach: "#ffbd59",
    Orange: "#ff914d",
    Brown: "#743c18",
  };
  // =============================================
  // 3. HELPER FUNCTIONS
  // =============================================
  function show(el) {
    if (el) el.style.display = "";
  }
  function hide(el) {
    if (el) el.style.display = "none";
  }
  function animatePriceChange(targetElement, start, end, duration = 500) {
    let startTime = null;
    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const currentValue = start + (end - start) * progress;
      targetElement.textContent = currentValue.toFixed(2);
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  function priceOf(groupKey, chosenValue) {
    if (!chosenValue) return 0;
    const list = PRICE_VARIANTS[groupKey] || [];
    const found = list.find((item) => item.value === chosenValue);
    return found ? found.price : 0;
  }
  function debounce(func, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  }
  function dataURLtoFile(dataurl, filename) {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  function isBoardCustomized(boardState) {
    return (
        (boardState.color && boardState.color !== "None") ||
        (boardState.image || boardState.imageUrl) ||
        (boardState.text && boardState.text.trim() !== "")
    );
  }

  function disableBodyScroll() {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.dataset.scrollY = scrollY; // save position
  }

  function enableBodyScroll() {
    const scrollY = document.body.dataset.scrollY || 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    window.scrollTo(0, parseInt(scrollY, 10));
    document.body.removeAttribute('data-scrollY');
  }
  
  function findScrollableParent(el) {
    while (el) {
      const style = window.getComputedStyle(el);
      const overflow = style.overflow || style.overflowY;
      if (overflow === 'auto' || overflow === 'scroll') {
        if (el.scrollHeight > el.clientHeight) {
          return el;
        }
      }
      el = el.parentElement;
    }
    return document.documentElement; // fallback to html/body
  }

  // =============================================
  // 4. IMAGE UPLOAD HANDLING
  // =============================================
  const isDisplayableImage = (file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    return NON_EDITABLE_EXTS.includes(ext) || file.type === "application/pdf";
  };

  function bindUploadForBoard(board) {
    const suf = board === 1 ? "b1" : "b2";
    const fileInput = document.getElementById(`upload-file-image-${suf}`);
    const chooseBtn = document.getElementById(`choose-file-btn-image-${suf}`);
    const artbox = document.getElementById(`upload-artbox-image-${suf}`);
    const previewBox = document.getElementById(`preview-box-image-${suf}`);
    const previewImg = document.getElementById(`preview-image-image-${suf}`);
    const deleteBtn = document.getElementById(`delete-image-${suf}`);
    const errorMsg = document.getElementById(`upload-file-error-image-${suf}`);
    if (
      !fileInput ||
      !chooseBtn ||
      !artbox ||
      !previewBox ||
      !previewImg ||
      !deleteBtn ||
      !errorMsg
    )
      return;

    // freshen inputs to clear old listeners
    const freshChoose = chooseBtn.cloneNode(true);
    const freshInput = fileInput.cloneNode(true);
    chooseBtn.replaceWith(freshChoose);
    fileInput.replaceWith(freshInput);

    freshChoose.addEventListener("click", (e) => {
      e.preventDefault();
      freshInput.click();
    });

    freshInput.addEventListener("change", (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const allowed = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "image/svg+xml",
      ];
      if (!allowed.includes(file.type)) {
        errorMsg.textContent = "Please upload .jpg, .jpeg, .png, .webp or .svg";
        errorMsg.classList.add("active");
        freshInput.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        previewImg.src = ev.target.result;
        artbox.style.display = "none";
        previewBox.style.display = "block";
        errorMsg.classList.remove("active");

        // ⬇️ Board-aware upload into canvas/state
        designer.handleImageUpload({ target: { files: [file] } }, board);
      };
      reader.readAsDataURL(file);
    });

    deleteBtn.addEventListener("click", () => {
      freshInput.value = "";
      artbox.style.display = "";
      previewBox.style.display = "none";
      previewImg.src = "";
      errorMsg.classList.remove("active");

      const bs = state[`board${board}`];
      bs.image = null;
      bs.imageUrl = null;
      bs.imageTransform = null;

      if (state.currentBoard === board && designer?.backgroundImage) {
        designer.canvas.remove(designer.backgroundImage);
        designer.backgroundImage = null;
        designer.canvas.renderAll();
        designer.updateFinishButtonState();
      }
    });
  }

  // =============================================
  // 5. DROPDOWN HANDLING
  // =============================================
  function initDropdownDefaults() {
    document.querySelectorAll(".select-dropdown").forEach((dd) => {
      const preSel = dd.querySelector("li.selected");
      const header = dd.querySelector(".selected-text");
      if (preSel && header) {
        header.textContent = preSel.textContent;
        const stateKey = dd.dataset.stateKey;
        if (stateKey && preSel.dataset.value) {
          const board = dd.id.includes("-b1")
            ? "board1"
            : dd.id.includes("-b2")
            ? "board2"
            : `board${state.currentBoard}`;
          state[board][stateKey] = preSel.dataset.value;
        }
      }
    });
  }
  function dropdownClickHandler(evt) {
    const dropdown = evt.target.closest(".select-dropdown");
    if (!dropdown) {
      document
        .querySelectorAll(".dropdown-options")
        .forEach((list) => hide(list));
      return;
    }
    const list = dropdown.querySelector(".dropdown-options");
    const header = dropdown.querySelector(".selected-text");
    const stateKey = dropdown.dataset.stateKey;
    const optionEl = evt.target.closest("li[data-value]");
    document.querySelectorAll(".dropdown-options").forEach((other) => {
      if (other !== list) hide(other);
    });
    if (optionEl) {
      const value = optionEl.dataset.value;
      if (!value) return;
      list
        .querySelectorAll("li")
        .forEach((li) => li.classList.remove("selected"));
      optionEl.classList.add("selected");
      header.textContent = optionEl.textContent;
      dropdown.classList.add("variant-picked");
      // Apply font class to selected-text if it's a font dropdown
      if (header) {
        // Remove existing font- classes
        header.classList.forEach((cls) => {
          if (cls.startsWith("font-")) header.classList.remove(cls);
        });
        // Add the new font- class from the selected li
        optionEl.classList.forEach((cls) => {
          if (cls.startsWith("font-")) header.classList.add(cls);
        });
      }
      if (stateKey) {
        const boardState = state[`board${state.currentBoard}`];
        boardState[stateKey] = value;
        const targetElement = dropdown;
        if (targetElement) {
          const customEvent = new Event("change");
          customEvent.target = { value: value };
          targetElement.dispatchEvent(customEvent);
        }
        switch (stateKey) {
          case "color":
            designer.updateBackgroundColor(value);
            break;
          case "font":
            designer.updateFont(value);
            break;
        }
        recalcPrice();
      }
      hide(list);
      return;
    }
    list.style.display = list.style.display === "block" ? "none" : "block";
  }
  // =============================================
  // 6. TEXT COLOR SELECTION
  // =============================================
  function setupTextColorSelection(board) {
    const suf = board === 1 ? "b1" : "b2";
    const swatchContainer = document.querySelector(
      "#text-color-variant-" + suf + " .letter-color-variants__list"
    );
    const selectedColor = document.getElementById("selected-text-color-" + suf);
    if (!swatchContainer || !selectedColor) return;
    swatchContainer.addEventListener("click", (event) => {
      const clickedSwatch = event.target.closest(".sticker-variant");
      if (!clickedSwatch || clickedSwatch.classList.contains("active")) return;
      swatchContainer
        .querySelectorAll(".sticker-variant")
        .forEach((el) => el.classList.remove("active"));
      clickedSwatch.classList.add("active");
      const value = clickedSwatch.dataset.color;
      if (!value) return;
      selectedColor.textContent = value;
      const boardState = state[`board${board}`];
      boardState.fontColor = value;
      const customEvent = new Event("change");
      customEvent.target = { value: value };
      document
        .querySelector("#text-color-variant-" + suf)
        .dispatchEvent(customEvent);
      if (state.currentBoard === board) {
        designer.updateFontColor(value);
      }
    });
  }
  // =============================================
  // 7. TEXT INPUT HANDLING
  // =============================================
  function handleTextInput(board) {
    const suf = board === 1 ? "b1" : "b2";
    const input = document.getElementById("text-input-" + suf);
    const inputDone = document.getElementById(`text-input-${suf}-done`);
    const counter = document.getElementById("text-input-char-count-" + suf);
    const error = document.getElementById("text-input-error-bar-" + suf);
    const wrapper = input?.parentElement;
    if (!input || !counter || !error || !wrapper) return;
    const MAX = 200;
    const refresh = () => {
      if (input.value.length > MAX) {
        input.value = input.value.slice(0, MAX);
      }
      const boardState = state[`board${board}`];
      boardState.text = input.value;
      const remaining = MAX - input.value.length;
      counter.textContent = `${remaining} character(s) left`;
      if (input.value.trim()) {
        error.style.display = "none";
        wrapper.classList.add("variant-picked");
        inputDone.classList.add("active");
      } else {
        wrapper.classList.remove("variant-picked");
        inputDone.classList.remove("active");
      }
      if (state.currentBoard === board && designer) {
        designer.updateText(input.value);
      }
    };
    const blurred = () => {
      inputDone.classList.remove("active");
      designer.canvas.discardActiveObject();
      designer.canvas.requestRenderAll();
    }
    input.addEventListener("input", refresh);
    input.addEventListener("blur", blurred);
    refresh(); // Initial call to set counter
  }
  // =============================================
  // 8. NOTES HANDLING
  // =============================================
  function handleNotesInput() {
    const input = document.getElementById("notes-input");
    const counter = document.getElementById("notes-char-count");
    if (!input || !counter) return;
    const MAX = 200;
    const refresh = () => {
      if (input.value.length > MAX) {
        input.value = input.value.slice(0, MAX);
      }
      state.notes = input.value;
      const remaining = MAX - input.value.length;
      counter.textContent = `${remaining} character(s) left`;
    };
    refresh();
    input.addEventListener("input", refresh);
  }
  // =============================================
  // 9. PROTECTION SELECTION
  // =============================================
  function setupProtectionSelection() {
    const variant = document.getElementById("protection-variant");
    if (!variant) return;
    variant.addEventListener("click", (event) => {
      const selected = event.target.closest(".sticker-variant");
      if (!selected || !variant.contains(selected)) return;
      const list = selected.closest(".sticker-variants__list");
      if (!list) return;
      list.querySelectorAll(".sticker-variant").forEach((el) => {
        el.classList.toggle("active", el === selected);
      });
      const value =
        selected.dataset.protection ||
        selected.getAttribute("data-protection") ||
        "None";
      state.protection = value;
      recalcPrice();
    });
  }
  // =============================================
  // 10. PRICE CALCULATION
  // =============================================
  function recalcPrice() {
    let total = BASE_PRICE;
    total += priceOf("protection", state.protection) || 0;
    total += state.extraForBoard2;
    total = Math.round(total * 100) / 100;
    const priceEl = document.getElementById("pcs-final-price");
    if (priceEl) {
      const prevValue = parseFloat(priceEl.textContent) || 0;
      if (prevValue !== total) {
        animatePriceChange(priceEl, prevValue, total);
      }
    }
    state.price = total;
  }
  // =============================================
  // 11. DELETE PREVIEW
  // =============================================
  const deleteBannerImage = document.getElementById("delete-banner-image");
  deleteBannerImage.addEventListener("click", (e) => {
    e.preventDefault();

    // Reset sidebar variants
    resetSidebarVariants();

    // Reset canvas properly without disposing it
    if (designer) {
      designer.handleRemoveAllImproved(); // Use improved method
    }

    // Switch to Board 1
    state.currentBoard = 1;

    // Update UI to show Board 1 is selected
    document.querySelectorAll(".board-tab").forEach((tab) => {
      const boardNum = parseInt(tab.dataset.board, 10);
      tab.classList.toggle("active", boardNum === 1);
    });

    // Show Board 1 variants and hide Board 2 variants
    document.getElementById("image-variant-b1").style.display = "";
    document.getElementById("variants-b1").style.display = "";
    document.getElementById("image-variant-b2").style.display = "none";
    document.getElementById("variants-b2").style.display = "none";

    // Hide preview and show upload area
    const previewBox = document.getElementById("preview-box");
    const mainVariants = document.getElementById("main-variants");
    const uploadArtbox = document.getElementById("upload-artbox");
    const btnEditImage = document.getElementById("btnEditImage");

    if (previewBox) {
      previewBox.style.display = "none";
      previewBox.classList.remove("non-editable-image");
    }
    if (mainVariants) mainVariants.classList.remove("active");
    if (uploadArtbox) uploadArtbox.style.display = "";
    if (btnEditImage) btnEditImage.style.display = "inline";

    // Clear preview images
    const previewImage1 = document.getElementById("preview-image1");
    const previewImage2 = document.getElementById("preview-image2");
    if (previewImage1) previewImage1.src = "";
    if (previewImage2) previewImage2.src = "";
    const preview2Wrap = document.querySelector(".board-preview2");
    if (preview2Wrap) preview2Wrap.style.display = "none";

    // Re-render the canvas for Board 1
    if (designer) {
      designer.renderBoardFromState(1);
    }
  });

  // =============================================
  // 12. RESET SIDEBAR VARIANTS
  // =============================================
  function resetSidebarVariants() {
    // Reset image uploads for both boards
    ["b1", "b2"].forEach((suf) => {
      const fileInput = document.getElementById(`upload-file-image-${suf}`);
      const artbox = document.getElementById(`upload-artbox-image-${suf}`);
      const previewBox = document.getElementById(`preview-box-image-${suf}`);
      const previewImg = document.getElementById(`preview-image-image-${suf}`);
      const errorMsg = document.getElementById(
        `upload-file-error-image-${suf}`
      );

      if (fileInput) fileInput.value = "";
      if (artbox) artbox.style.display = "";
      if (previewBox) previewBox.style.display = "none";
      if (previewImg) previewImg.src = "";
      if (errorMsg) errorMsg.classList.remove("active");
    });

    // Reset state for both boards
    state.board1 = {
      color: "None",
      image: null,
      imageUrl: null,
      text: "",
      font: "Anton",
      fontColor: "Black",
      imageTransform: null,
      textTransform: null,
    };

    state.board2 = {
      color: "None",
      image: null,
      imageUrl: null,
      text: "",
      font: "Anton",
      fontColor: "Black",
      imageTransform: null,
      textTransform: null,
    };

    state.editedBoard1 = null;
    state.editedBoard2 = null;
    state.customizeBoard2 = false; // Reset to false to re-trigger confirmation on switch
    state.extraForBoard2 = 0;

    // Reset dropdowns (color, font)
    ["b1", "b2"].forEach((suf) => {
      ["color", "font"].forEach((key) => {
        const dropdown = document.getElementById(`${key}-select-${suf}`);
        if (dropdown) {
          const list = dropdown.querySelector(".dropdown-options");
          const header = dropdown.querySelector(".selected-text");
          if (list && header) {
            list
              .querySelectorAll("li")
              .forEach((li) => li.classList.remove("selected"));
            const firstOption = list.querySelector("li[data-value]");
            if (firstOption) {
              firstOption.classList.add("selected");
              header.textContent = firstOption.textContent.trim();
              if (key === "font") {
                header.classList.forEach((cls) => {
                  if (cls.startsWith("font-")) header.classList.remove(cls);
                });
                firstOption.classList.forEach((cls) => {
                  if (cls.startsWith("font-")) header.classList.add(cls);
                });
              }
            }
            hide(list);
            dropdown.classList.remove("variant-picked");
          }
        }
      });
    });

    // Reset text input
    ["b1", "b2"].forEach((suf) => {
      const text = document.getElementById("text-input-" + suf);
      if (text) {
        text.value = "";
        const counter = document.getElementById("text-input-char-count-" + suf);
        if (counter) counter.textContent = "200 character(s) left";
        const wrapper = text.parentElement;
        if (wrapper) wrapper.classList.remove("variant-picked");
        const error = document.getElementById("text-input-error-bar-" + suf);
        if (error) error.style.display = "none";
      }
    });

    // Reset font color selection
    ["b1", "b2"].forEach((suf) => {
      const colorList = document.querySelectorAll(
        "#text-color-variant-" +
          suf +
          " .letter-color-variants__list .sticker-variant"
      );
      if (colorList.length) {
        colorList.forEach((el) => el.classList.remove("active"));
        const first = colorList[0];
        first.classList.add("active");
        const selTxt = document.getElementById("selected-text-color-" + suf);
        if (selTxt) selTxt.textContent = first.dataset.color || "Black";
      }
    });

    // Clear errors
    document.querySelectorAll(".error-bar").forEach((el) => hide(el));
    document
      .querySelectorAll(".pcs-variant")
      .forEach((el) => el.classList.remove("has-error"));

    // Recalculate price
    recalcPrice();

    // Update canvas to reflect reset state
    if (designer) {
      designer.updateBackgroundColor("None");
      designer.updateText("");
      designer.updateFont("Anton");
      designer.updateFontColor("Black");
    }
  }
  // =============================================
  // 13. FORM VALIDATION
  // =============================================
  function validateForm() {
    let firstInvalid = null;
    function toggleError(id, show, message = "") {
      const bar = document.getElementById(id);
      if (!bar) return;
      if (message) bar.textContent = message;
      bar.style.display = show ? "block" : "none";
      const variantBox = bar.closest(".pcs-variant");
      if (variantBox) variantBox.classList.toggle("has-error", show);
      if (show && !firstInvalid) firstInvalid = variantBox || bar;
    }
    const suf = state.currentBoard === 1 ? "b1" : "b2";
    const textEl = document.getElementById("text-input-" + suf);
    const textVal = (textEl ? textEl.value : "").trim();
    const boardState = state[`board${state.currentBoard}`];
    boardState.text = textVal || null;
    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  }
  // =============================================
  // 14. RESET FORM
  // =============================================
  function resetCalculatorForm() {
    // Reset image upload
    const fileInput = document.getElementById("upload-file-image");
    if (fileInput) fileInput.value = "";
    const artbox = document.getElementById("upload-artbox-image");
    const previewBox = document.getElementById("preview-box-image");
    if (artbox) artbox.style.display = "";
    if (previewBox) previewBox.style.display = "none";

    // Ensure preview-box is hidden and upload-artbox is shown
    const mainPreviewBox = document.getElementById("preview-box");
    const mainVariants = document.getElementById("main-variants");
    const uploadArtbox = document.getElementById("upload-artbox");
    if (mainPreviewBox) mainPreviewBox.style.display = "none";
    if (mainVariants) mainVariants.classList.remove("active");
    if (uploadArtbox) uploadArtbox.style.display = "";
    // Clear the preview images
    const previewImage1 = document.getElementById("preview-image1");
    const previewImage2 = document.getElementById("preview-image2");
    if (previewImage1) previewImage1.src = "";
    if (previewImage2) previewImage2.src = "";

    document.querySelectorAll(".select-dropdown").forEach((dd) => {
      const list = dd.querySelector(".dropdown-options");
      const header = dd.querySelector(".selected-text");
      if (!list || !header) return;
      list
        .querySelectorAll("li")
        .forEach((li) => li.classList.remove("selected"));
      const firstOption = list.querySelector("li[data-value]");
      if (firstOption) {
        firstOption.classList.add("selected");
        header.textContent = firstOption.textContent.trim();
        const stateKey = dd.dataset.stateKey;
        if (stateKey) {
          const board = dd.id.includes("-b1")
            ? "board1"
            : dd.id.includes("-b2")
            ? "board2"
            : "board1";
          state[board][stateKey] = firstOption.dataset.value;
        }
      }
      hide(list);
      dd.classList.remove("variant-picked");
    });
    const colorList = document.querySelectorAll(
      "[id^='text-color-variant-'] .letter-color-variants__list .sticker-variant"
    );
    if (colorList.length) {
      colorList.forEach((el) => el.classList.remove("active"));
      const first = colorList[0];
      first.classList.add("active");
      state.board1.fontColor = first.dataset.color || "Black";
      state.board2.fontColor = first.dataset.color || "Black";
      const selTxt = document.getElementById("selected-text-color-b1");
      if (selTxt) selTxt.textContent = state.board1.fontColor;
      const selTxt2 = document.getElementById("selected-text-color-b2");
      if (selTxt2) selTxt2.textContent = state.board2.fontColor;
    }
    ["b1", "b2"].forEach((suf) => {
      const text = document.getElementById("text-input-" + suf);
      if (text) {
        text.value = "";
        const counter = document.getElementById("text-input-char-count-" + suf);
        if (counter) counter.textContent = "200 character(s) left";
        if (designer && state.currentBoard === (suf === "b1" ? 1 : 2))
          designer.updateText("");
      }
    });
    const notes = document.getElementById("notes-input");
    if (notes) {
      notes.value = "";
      state.notes = null;
      const notesCount = document.getElementById("notes-char-count");
      if (notesCount) notesCount.textContent = "200 character(s) left";
    }
    const protectionWrap = document.getElementById("protection-variant");
    if (protectionWrap) {
      const items = protectionWrap.querySelectorAll(".sticker-variant");
      items.forEach((el) => el.classList.remove("active"));
      if (items.length) {
        items[0].classList.add("active");
        state.protection = "None";
      }
    }
    state.customizeBoard2 = false;
    state.extraForBoard2 = 0;
    state.board1.image = null;
    state.board2.image = null;
    state.editedBoard1 = null;
    state.editedBoard2 = null;
    document.querySelectorAll(".error-bar").forEach((el) => hide(el));
    document
      .querySelectorAll(".pcs-variant")
      .forEach((el) => el.classList.remove("has-error"));
    recalcPrice();
    if (designer) {
      designer.drawClipPath();
    }
  }
  // =============================================
  // 15. ADD TO CART FUNCTIONALITY
  // =============================================
  function setupApippaAddToCart() {
    const addToCartBtn = document.getElementById("pcs-add-to-cart");
    if (!addToCartBtn) return;
    addToCartBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      if (!validateForm()) {
        console.log("Form invalid – fix the highlighted fields.");
        return;
      }
      console.log("Form valid – adding to cart:", state);
      addToCartBtn.disabled = true;
      addToCartBtn.classList.add("loading");
      addToCartBtn.innerHTML = `
        <div class="pcs-loader"></div>
        <p class="pcs-message"></p>
        <div class="dot-falling"></div>
      `;
      const messageEl = addToCartBtn.querySelector(".pcs-message");
      const messages = ["Adding to cart", "GETTING THINGS READY"];
      let messageIndex = 0;
      let stopTypingLoop = false;
      const startTypingLoop = async () => {
        while (!stopTypingLoop) {
          const message = messages[messageIndex % messages.length];
          await typeText(messageEl, message);
          await new Promise((resolve) => setTimeout(resolve, 2000));
          await deleteText(messageEl);
          messageIndex++;
        }
      };
      startTypingLoop();
      const loadingBar = addToCartBtn.querySelector(".pcs-loader");
      let progress = 0;
      const target = 99;
      const interval = setInterval(() => {
        if (progress < 50) {
          progress += 2;
        } else if (progress < 80) {
          progress += (80 - progress) * 0.05;
        } else if (progress < target - 0.2) {
          progress += (target - progress) * 0.05;
        } else {
          progress = target - 0.01;
          clearInterval(interval);
        }
        loadingBar.style.transform = `scaleX(${Math.floor(progress) / 100})`;
      }, 80);
      try {
        // Set extraForBoard2 based on customizeBoard2
        state.extraForBoard2 = state.customizeBoard2 ? EXTRA_BOARD2_PRICE : 0;
        recalcPrice(); // Ensure price reflects the latest state

        const board2Active = state.customizeBoard2 && isBoardCustomized(state.board2);

        const requestData = {
          calculatorId: "fea8a85454164",
          productId: 7741120380976,
          properties: {
            "Board1 Color": state.board1.color,
            "Board1 Text": state.board1.text || "",
            "Board1 Font": state.board1.text?.trim() ? state.board1.font : "",
            "Board1 Font Color": state.board1.text?.trim()
              ? state.board1.fontColor
              : "",
            "Board2 Color": board2Active ? state.board2.color : "",
            "Board2 Text": board2Active ? state.board2.text || "" : "",
            "Board2 Font":
              board2Active && state.board2.text?.trim()
                ? state.board2.font
                : "",
            "Board2 Font Color":
              board2Active && state.board2.text?.trim()
                ? state.board2.fontColor
                : "",
            "Add Additional Layer of Protection": state.protection,
            "Instructions or Notes": state.notes || "",
            "Board1 Image": state.board1.image?.name || "",
            "Board2 Image": board2Active ? state.board2.image?.name || "" : "",
            "Board1 Edited": state.editedBoard1?.name || "",
            "Board2 Edited": board2Active
              ? state.editedBoard2?.name || ""
              : "Same as Board1",
            "Is Board 2?": board2Active ? "Yes" : "No",
          },
        };
        console.log("Request Data: ", requestData);
        const variantRes = await fetch(
          "https://api.custompricecalculator.com/api/v1/variant",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestData),
          }
        );
        const variantId = await variantRes.json();
        const shopifyVariantId =
          typeof variantId === "string"
            ? variantId
            : variantId?.variantId || variantId?.id;
        if (!shopifyVariantId) throw new Error("Invalid variant ID");
        const formData = new FormData();
        formData.append("id", shopifyVariantId);
        formData.append("quantity", 1);
        Object.entries(requestData.properties).forEach(([key, value]) => {
          formData.append(`properties[${key}]`, value);
        });
        if (state.board1.image) {
          formData.append("properties[Board1 Image]", state.board1.image);
        }
        if (state.customizeBoard2 && state.board2.image) {
          formData.append("properties[Board2 Image]", state.board2.image);
        }
        if (state.editedBoard1) {
          formData.append("properties[Board1 Edited]", state.editedBoard1);
        }
        if (state.customizeBoard2 && state.editedBoard2) {
          formData.append("properties[Board2 Edited]", state.editedBoard2);
        } else if (!state.customizeBoard2 && state.editedBoard1) {
          // If Board 2 is not customized, do not append Board2 Edited to avoid confusion
          // The "Same as Board1" is already set in properties
        }
        const cartAddRes = await fetch("/cart/add.js", {
          method: "POST",
          body: formData,
        });
        const cartAddJson = await cartAddRes.json();
        console.log("Add to Cart Response:", cartAddJson);
        localStorage.setItem(
          `patched-cart-item-${cartAddJson.id}`,
          JSON.stringify({
            id: cartAddJson.id,
            title: cartAddJson.product_title,
            image: cartAddJson.image,
            price: cartAddJson.price,
          })
        );
        if (!cartAddRes.ok) throw new Error("Failed to add to cart");
        const cartJson = {
          items: [cartAddJson],
          item_count: 1,
          total_price: cartAddJson.final_price,
          original_total_price: cartAddJson.original_price,
          total_discount: 0,
        };
        const timestamp = new Date().getTime();
        await new Promise((resolve) => setTimeout(resolve, 200));
        const sectionHtml = await fetch(
          `/cart?section_id=mini-cart&t=${timestamp}`
        ).then((res) => res.text());
        const fakeDiv = document.createElement("div");
        fakeDiv.innerHTML = sectionHtml;
        const miniCartHTML = fakeDiv.querySelector("cart-drawer")?.outerHTML;
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && miniCartHTML) {
          cartDrawer.dispatchEvent(
            new CustomEvent("cart:refresh", {
              bubbles: true,
              detail: {
                cart: {
                  ...cartJson,
                  sections: {
                    "mini-cart": miniCartHTML,
                  },
                },
                openMiniCart: true,
              },
            })
          );
        }
        setTimeout(() => {
          const lineItems = cartDrawer.querySelectorAll("line-item");
          lineItems.forEach((lineItem) => {
            const desktopPriceContainer = lineItem.querySelector(
              ".line-item__price-list-container .price"
            );
            const mobilePriceContainer = lineItem.querySelector(
              ".product-item-meta__price-list-container .price-list.hidden-tablet-and-up .price"
            );
            if (!desktopPriceContainer && !mobilePriceContainer) return;
            const priceText = (
              desktopPriceContainer || mobilePriceContainer
            ).textContent.trim();
            const isFree = /Free|\$0\.00/.test(priceText);
            let variantId;
            const productLink = lineItem.querySelector("a[href*='?variant=']");
            if (productLink?.href.includes("variant=")) {
              variantId = productLink.href.split("variant=")[1].split("&")[0];
            }
            if (!variantId) {
              const removeBtn = lineItem.querySelector(
                "a[href*='/cart/change?id=']"
              );
              const removeHref = removeBtn?.getAttribute("href") || "";
              const match = removeHref.match(/id=(\d+):/);
              variantId = match?.[1];
            }
            if (isFree && variantId == shopifyVariantId) {
              const matchingItem = cartJson.items.find(
                (item) => String(item.id) === variantId
              );
              if (matchingItem) {
                const correctPrice = (matchingItem.price / 100).toFixed(2);
                const imageWrapper = lineItem.querySelector(
                  ".line-item__image-wrapper"
                );
                const productThumbnail = imageWrapper.querySelector(
                  "img.line-item__image"
                );
                const productTitle = lineItem.querySelector(
                  ".product-item-meta .product-item-meta__title"
                );
                if (!productThumbnail) {
                  const imgSrc = document.querySelector(
                    ".product__thumbnail-item .product__thumbnail img"
                  )?.src;
                  if (imgSrc) {
                    imageWrapper.innerHTML += `
                      <img src="${imgSrc}" alt="" srcset="${imgSrc}" width="1024" height="1024" width="1024" loading="lazy" sizes="(max-width: 740px) 80px, 92px" class="line-item__image">
                    `;
                  }
                }
                if (productTitle.textContent.trim() === "") {
                  productTitle.textContent = document.querySelector(
                    ".product__info .product-meta__title"
                  ).textContent;
                }
                const updatedPriceHTML = `
                  <span class="visually-hidden">Sale price</span>
                  $${correctPrice}
                `;
                if (desktopPriceContainer) {
                  desktopPriceContainer.innerHTML = updatedPriceHTML;
                }
                if (mobilePriceContainer) {
                  mobilePriceContainer.innerHTML = updatedPriceHTML;
                }
              }
            }
          });
        }, 300);
        setTimeout(async () => {
          const cartJson = await fetch("/cart.js").then((res) => res.json());
          const cartCountEl = document.querySelector("cart-count");
          if (cartCountEl) {
            cartCountEl.textContent = cartJson.item_count;
          }
        }, 300);
        setTimeout(() => {
          const drawerContent = document.querySelector(
            "#shopify-section-mini-cart .drawer__content"
          );
          if (!drawerContent) return;
          const confetti = document.createElement("dotlottie-player");
          confetti.className = "mini-cart-lottie";
          confetti.src =
            "//cdn.shopify.com/s/files/1/0582/5400/0176/files/confetti.json?v=1748024242";
          confetti.setAttribute("background", "transparent");
          confetti.setAttribute("speed", "1");
          confetti.setAttribute("style", "width: 300px; height: 300px");
          confetti.setAttribute("direction", "1");
          confetti.setAttribute("playmode", "normal");
          confetti.setAttribute("autoplay", "");
          drawerContent.prepend(confetti);
          setTimeout(() => {
            confetti.remove();
          }, 3000);
        }, 1000);
      } catch (err) {
        console.error("Apippa Add to Cart Error:", err);
        alert("There was a problem adding your item to the cart.");
      } finally {
        addToCartBtn.disabled = false;
        addToCartBtn.classList.remove("loading");
        addToCartBtn.innerHTML = "Add to Cart";
        stopTypingLoop = true;
        // Reset sidebar and canvas to match deleteBannerImage behavior
        resetSidebarVariants();
        // Reset notes
        const notes = document.getElementById("notes-input");
        if (notes) {
          notes.value = "";
          state.notes = null;
          const notesCount = document.getElementById("notes-char-count");
          if (notesCount) notesCount.textContent = "200 character(s) left";
        }
        // Reset protection
        const protectionWrap = document.getElementById("protection-variant");
        if (protectionWrap) {
          const items = protectionWrap.querySelectorAll(".sticker-variant");
          items.forEach((el) => el.classList.remove("active"));
          if (items.length) {
            items[0].classList.add("active");
            state.protection = "None";
          }
        }
        // Switch to Board 1
        state.currentBoard = 1;
        // Update tabs
        document.querySelectorAll(".board-tab").forEach((tab) => {
          const boardNum = parseInt(tab.dataset.board, 10);
          tab.classList.toggle("active", boardNum === 1);
        });
        // Show Board 1 variants and hide Board 2 variants
        document.getElementById("image-variant-b1").style.display = "";
        document.getElementById("variants-b1").style.display = "";
        document.getElementById("image-variant-b2").style.display = "none";
        document.getElementById("variants-b2").style.display = "none";
        // Hide preview and show upload area
        const previewBox = document.getElementById("preview-box");
        const mainVariants = document.getElementById("main-variants");
        const uploadArtbox = document.getElementById("upload-artbox");
        const btnEditImage = document.getElementById("btnEditImage");
        if (previewBox) {
          previewBox.style.display = "none";
          previewBox.classList.remove("non-editable-image");
        }
        if (mainVariants) mainVariants.classList.remove("active");
        if (uploadArtbox) uploadArtbox.style.display = "";
        if (btnEditImage) btnEditImage.style.display = "inline";
        // Clear preview images
        const previewImage1 = document.getElementById("preview-image1");
        const previewImage2 = document.getElementById("preview-image2");
        if (previewImage1) previewImage1.src = "";
        if (previewImage2) previewImage2.src = "";
        const preview2Wrap = document.querySelector(".board-preview2");
        if (preview2Wrap) preview2Wrap.style.display = "none";
        // Reset canvas
        if (designer) {
          designer.handleRemoveAllImproved();
          designer.renderBoardFromState(1);
        }
      }
    });
  }
  // Text animation helpers
  function typeText(el, text, speed = 60) {
    return new Promise((resolve) => {
      let i = 0;
      const typeInterval = setInterval(() => {
        el.textContent += text[i];
        i++;
        if (i >= text.length) {
          clearInterval(typeInterval);
          resolve();
        }
      }, speed);
    });
  }
  function deleteText(el, speed = 40) {
    return new Promise((resolve) => {
      const delInterval = setInterval(() => {
        el.textContent = el.textContent.slice(0, -1);
        if (el.textContent.length === 0) {
          clearInterval(delInterval);
          resolve();
        }
      }, speed);
    });
  }
  // =============================================
  // 16. BOARD SWITCHING
  // =============================================
  function handleCustomizeBoard2Modal(resolve) {
    const customizeBoard2Modal = document.getElementById(
      "customize-board2-popup"
    );
    const cancelCustomize = document.getElementById("cancel-customize-board2");
    const confirmCustomize = document.getElementById(
      "confirm-customize-board2"
    );

    if (!customizeBoard2Modal || !cancelCustomize || !confirmCustomize) {
      console.error("Modal or buttons not found");
      resolve(false);
      return;
    }

    // Show the modal
    customizeBoard2Modal.classList.add("active");
    disableBodyScroll()

    // Cancel button handler
    cancelCustomize.addEventListener(
      "click",
      () => {
        customizeBoard2Modal.classList.remove("active");
        resolve(false);
      },
      { once: true }
    );

    // Confirm button handler
    confirmCustomize.addEventListener(
      "click",
      () => {
        customizeBoard2Modal.classList.remove("active");
        document.querySelector(".sidebar").scrollTo({top:0, behaviour: "smooth"})
        resolve(true);
      },
      { once: true }
    );
  }

  function setupBoardSwitching() {
    const tabs = document.querySelectorAll(".board-tab");

    tabs.forEach((tab) => {
      tab.addEventListener("click", async (e) => {
        const newBoard = parseInt(e.currentTarget.dataset.board, 10);
        if (newBoard === state.currentBoard) return;

        // Persist current board first
        saveCurrentBoardState();

        // First-time Board 2?
        if (newBoard === 2 && !state.customizeBoard2) {
          // Use the modal handler function
          const confirmed = await new Promise((resolve) => {
            handleCustomizeBoard2Modal(resolve);
          });

          if (!confirmed) return;

          state.customizeBoard2 = true;
          state.extraForBoard2 = EXTRA_BOARD2_PRICE;
          // Give Board 2 its own independent state
          state.board2 = {
            color: "None",
            image: null,
            imageUrl: null,
            text: "",
            font: "Anton",
            fontColor: "Black",
            imageTransform: null,
            textTransform: null,
          };
          document.getElementById("image-variant-b2").style.display = "";
          document.getElementById("variants-b2").style.display = "";
          recalcPrice();
        }

        state.currentBoard = newBoard;
        document.getElementById("image-variant-b1").style.display =
          newBoard === 1 ? "" : "none";
        document.getElementById("image-variant-b2").style.display =
          newBoard === 2 && state.customizeBoard2 ? "" : "none";
        document.getElementById("variants-b1").style.display =
          newBoard === 1 ? "" : "none";
        document.getElementById("variants-b2").style.display =
          newBoard === 2 && state.customizeBoard2 ? "" : "none";

        // Tab UI
        document
          .querySelectorAll(".board-tab")
          .forEach((t) =>
            t.classList.toggle(
              "active",
              parseInt(t.dataset.board, 10) === newBoard
            )
          );

        // Rebuild canvas from state
        await designer.renderBoardFromState(newBoard);
        // Sync the sidebar to that state
        loadCurrentBoardState();
      });
    });
  }

  function saveCurrentBoardState() {
    const suf = state.currentBoard === 1 ? "b1" : "b2";
    const boardState = state[`board${state.currentBoard}`];

    const colorSelect = document.getElementById("color-select-" + suf);
    if (colorSelect) {
      const sel = colorSelect.querySelector(".dropdown-options li.selected");
      if (sel) boardState.color = sel.dataset.value || "None";
    }

    const fontSelect = document.getElementById("font-select-" + suf);
    if (fontSelect) {
      const sel = fontSelect.querySelector(".dropdown-options li.selected");
      if (sel) boardState.font = sel.dataset.value || "Anton";
    }

    const textInput = document.getElementById("text-input-" + suf);
    if (textInput) boardState.text = textInput.value;

    const colorList = document.querySelector(
      "#text-color-variant-" +
        suf +
        " .letter-color-variants__list .sticker-variant.active"
    );
    if (colorList) boardState.fontColor = colorList.dataset.color || "Black";

    // Capture transforms from the live canvas objects
    if (designer) {
      if (designer.backgroundImage) {
        // keep a URL for reload; if it’s a FileReader dataURL, getSrc() works
        if (typeof designer.backgroundImage.getSrc === "function") {
          boardState.imageUrl = designer.backgroundImage.getSrc();
        }
        boardState.imageTransform = designer._snapshotObj(
          designer.backgroundImage
        );
      } else if (!boardState.imageUrl) {
        boardState.imageUrl = null;
        boardState.imageTransform = null;
      }

      if (designer.text) {
        boardState.textTransform = designer._snapshotObj(designer.text);
      } else {
        boardState.textTransform = null;
      }
    }
  }

  function syncSidebarToBoardState(boardIndex) {
    const bs = state[`board${boardIndex}`];

    const suf = boardIndex === 1 ? "b1" : "b2";
    const artbox = document.getElementById(`upload-artbox-image-${suf}`);
    const previewBox = document.getElementById(`preview-box-image-${suf}`);
    const previewImg = document.getElementById(`preview-image-image-${suf}`);

    if (bs.imageUrl) {
      if (artbox) artbox.style.display = "none";
      if (previewBox) previewBox.style.display = "block";
      if (previewImg) previewImg.src = bs.imageUrl;
    } else {
      if (artbox) artbox.style.display = "";
      if (previewBox) previewBox.style.display = "none";
      if (previewImg) previewImg.src = "";
    }

    // Text input + counter
    const textInput = document.getElementById("text-input-" + suf);
    const counter = document.getElementById("text-input-char-count-" + suf);
    const error = document.getElementById("text-input-error-bar-" + suf);
    if (textInput) {
      textInput.value = bs.text || "";
      const hasText = !!(bs.text && bs.text.trim());
      const wrapper = textInput.parentElement;
      if (wrapper) wrapper.classList.toggle("variant-picked", hasText);
      if (counter)
        counter.textContent = `${
          200 - textInput.value.length
        } character(s) left`;
      if (error) error.style.display = "none";
    }

    // Text color swatches
    const swatches = document.querySelectorAll(
      "#text-color-variant-" +
        suf +
        " .letter-color-variants__list .sticker-variant"
    );
    const sel = document.getElementById("selected-text-color-" + suf);
    const fc = bs.fontColor || "Black";
    swatches.forEach((el) =>
      el.classList.toggle("active", el.dataset.color === fc)
    );
    if (sel) sel.textContent = fc;

    // Color dropdown
    const colorDropdown = document.getElementById("color-select-" + suf);
    if (colorDropdown) {
      const header = colorDropdown.querySelector(".selected-text");
      const list = colorDropdown.querySelector(".dropdown-options");
      const val = bs.color || "None";
      if (list && header) {
        list
          .querySelectorAll("li")
          .forEach((li) =>
            li.classList.toggle("selected", li.dataset.value === val)
          );
        const li = list.querySelector(`li[data-value="${val}"]`);
        if (li) header.textContent = li.textContent.trim();
        colorDropdown.classList.toggle("variant-picked", val !== "None");
      }
    }

    // Font dropdown
    const fontDropdown = document.getElementById("font-select-" + suf);
    if (fontDropdown) {
      const header = fontDropdown.querySelector(".selected-text");
      const list = fontDropdown.querySelector(".dropdown-options");
      const val = bs.font || "Anton";
      if (list && header) {
        list
          .querySelectorAll("li")
          .forEach((li) =>
            li.classList.toggle("selected", li.dataset.value === val)
          );
        const li = list.querySelector(`li[data-value="${val}"]`);
        if (li) {
          header.textContent = li.textContent.trim();
          header.classList.forEach((c) => {
            if (c.startsWith("font-")) header.classList.remove(c);
          });
          li.classList.forEach((c) => {
            if (c.startsWith("font-")) header.classList.add(c);
          });
        }
        fontDropdown.classList.toggle(
          "variant-picked",
          !!(bs.text && bs.text.trim())
        );
      }
    }

    // Clear lingering errors
    document
      .querySelectorAll(".error-bar")
      .forEach((el) => (el.style.display = "none"));
    document
      .querySelectorAll(".pcs-variant")
      .forEach((el) => el.classList.remove("has-error"));
  }

  function loadCurrentBoardState() {
    const idx = state.currentBoard;
    const bs = state[`board${idx}`];

    // Sync sidebar UI
    syncSidebarToBoardState(idx);

    // Update canvas
    if (designer) {
      designer.canvas.clear();
      designer.canvas.clipPath = null;
      designer.backgroundRect = null;
      designer.backgroundImage = null;
      designer.text = null;
      designer.guidelineObjects = [];

      designer.drawClipPath();

      if (designer.backgroundRect)
        designer.backgroundRect.set({
          fill: COLOR_MAPPINGS[bs.color] || "transparent",
        });

      designer.updateFont(bs.font || "Anton");
      designer.updateFontColor(bs.fontColor || "Black");
      designer.updateText(bs.text || "");
      if (designer.text && bs.textTransform) {
        designer._applySnapshot(designer.text, bs.textTransform);
      }

      if (bs.imageUrl) {
        fabric.Image.fromURL(
          bs.imageUrl,
          (img) => {
            designer._placeImage(img, bs.imageTransform);
            designer.canvas.renderAll();
          },
          { crossOrigin: "anonymous" }
        );
      }

      designer.canvas.renderAll();
      designer.updateFinishButtonState();
    }
  }

  // =============================================
  // 17. CORNHOLE DESIGNER CLASS
  // =============================================
  class CornholeDesigner {
    constructor() {
      this.canvas = null;
      this.clipPath = null;
      this.backgroundRect = null;
      this.guidelineObjects = [];
      this.freezeLayout = false;
      this._renderToken = 0;
      this.backgroundImage = null;
      this.text = null;
      this.snappingEnabled = true;
      this._ro = null;
      this.icons = {
        resizeLeft:
          "https://cdn.shopify.com/s/files/1/0582/5400/0176/files/white-resize-left.webp",
        resizeRight:
          "https://cdn.shopify.com/s/files/1/0582/5400/0176/files/white-resize-right.webp",
        resizeHorizontal:
          "https://cdn.shopify.com/s/files/1/0582/5400/0176/files/white-resize-horizontal.webp",
        stackUp:
          "https://cdn.shopify.com/s/files/1/0582/5400/0176/files/stackup.webp",
        stackDown:
          "https://cdn.shopify.com/s/files/1/0582/5400/0176/files/stackdown.webp",
        rotate:
          "https://cdn.shopify.com/s/files/1/0582/5400/0176/files/rotate.webp",
      };
      this.init();
    }

    init() {
      const canvasContainer = document.getElementById("canvasContainer");
      if (!canvasContainer) {
        console.error("Canvas container not found");
        return;
      }

      canvasContainer.innerHTML = '<canvas id="stickerCanvas"></canvas>';
      this.canvas = new fabric.Canvas("stickerCanvas", {
        width: canvasContainer.offsetWidth,
        height: canvasContainer.offsetHeight,
        preserveObjectStacking: true,
        controlsAboveOverlay: true,
        selection: true,
        backgroundColor: "white",
      });

      this.setupCanvas();
      this.setupEventListeners();
      this.setupCustomControls();
      this.observeContainerResize(canvasContainer);
      this.updateCanvasSize();
      // Do NOT call drawClipPath here; let it be called when needed
      this.updateFinishButtonState();
    }

    observeContainerResize(container) {
      const ro = new ResizeObserver(debounce(() => this.handleResize(), 120));
      ro.observe(container);
      this._ro = ro;
    }

    setupCanvas() {
      if (!this.canvas) return;
      this.canvas.preserveObjectStacking = true;
      this.canvas.controlsAboveOverlay = true;
      this.canvas.selection = true;
    }

    setupEventListeners() {
      window.addEventListener(
        "resize",
        debounce(() => this.handleResize(), 300)
      );

      document.addEventListener("keydown", (e) => {
        const obj = this.canvas?.getActiveObject();
        if (!obj) return;
        const mod = e.ctrlKey || e.metaKey;

        if (mod && e.key === "]") {
          const cur = this.canvas.getObjects().indexOf(obj);
          const maxIdx = this._topCeilingIndex();
          if (cur < maxIdx) this.canvas.bringForward(obj);
          this._guardZ(obj);
          e.preventDefault();
        } else if (mod && e.key === "[") {
          const cur = this.canvas.getObjects().indexOf(obj);
          const minIdx = this._bgTopIndex() + 1;
          if (cur > minIdx) this.canvas.sendBackwards(obj);
          this._guardZ(obj);
          e.preventDefault();
        }
      });

      const getStartedBtn = document.getElementById("get-started-btn");
      if (getStartedBtn) {
        getStartedBtn.addEventListener("click", async () => {
          const designerTool = document.getElementById("designerTool");
          if (designerTool) {
            designerTool.classList.add("active");
            disableBodyScroll()
            await designer.renderBoardFromState(state.currentBoard);
          }
        });
      }
      const closeBtn = document.getElementById("designer-tool-close-btn");
      if (closeBtn) {
        closeBtn.addEventListener("click", () => {
          const popup = document.getElementById("close-tool-popup");
          if (popup) popup.classList.add("active");
        });
      }
      const cancelClose = document.getElementById("cancel-close-tool");
      if (cancelClose) {
        cancelClose.addEventListener("click", () => {
          const popup = document.getElementById("close-tool-popup");
          if (popup) popup.classList.remove("active");
        });
      }
      const confirmClose = document.getElementById("confirm-close-tool");
      if (confirmClose) {
        confirmClose.addEventListener("click", () => {
          const popup = document.getElementById("close-tool-popup");
          const designerTool = document.getElementById("designerTool");
          if (popup) popup.classList.remove("active");
          if (designerTool) {
            designerTool.classList.remove("active");
            enableBodyScroll()
          }
        });
      }
      const toggleGuides = document.getElementById("toggleGuides");
      if (toggleGuides) {
        toggleGuides.addEventListener("change", (e) => {
          this.toggleGuidelines(e.target.checked);
        });
      }
      const toggleSnap = document.getElementById("toggleSnap");
      if (toggleSnap) {
        toggleSnap.addEventListener("change", (e) => {
          this.snappingEnabled = e.target.checked;
        });
      }
      const btnEditImage = document.getElementById("btnEditImage");
      if (btnEditImage) {
        btnEditImage.addEventListener("click", async (e) => {
          const designerTool = document.getElementById("designerTool");
          if (designerTool) {
            designerTool.classList.add("active");
            disableBodyScroll()
            await designer.renderBoardFromState(state.currentBoard);
          }
        });
      }
      document
        .querySelectorAll(".align-btn")
        .forEach((btn) =>
          btn.addEventListener("click", (e) =>
            this.alignObjects(e.currentTarget.dataset.align)
          )
        );
      document.querySelectorAll(".text-align-btn").forEach((btn) =>
        btn.addEventListener("click", (e) => {
          const align = e.currentTarget.dataset.textAlign;
          const active = this.canvas.getActiveObject();
          if (active && (active.type === "text" || active.type === "textbox")) {
            active.set("textAlign", align);
            this.canvas.renderAll();
            // Ensure the state is updated immediately
            const boardState = state[`board${state.currentBoard}`];
            if (boardState.textTransform) {
              boardState.textTransform.textAlign = align;
            } else if (active.text) {
              boardState.textTransform = { textAlign: align };
            }
            saveCurrentBoardState();
          }
        })
      );
      const finishEditing = document.getElementById("finishEditing");
      if (finishEditing) {
        finishEditing.addEventListener("click", () => this.finishEditing());
      }
      ["b1", "b2"].forEach((suf) => {
        const colorSelect = document.getElementById("color-select-" + suf);
        if (colorSelect) {
          colorSelect.addEventListener("change", (e) => {
            if (e.target.value) this.updateBackgroundColor(e.target.value);
          });
        }
        const fontSelect = document.getElementById("font-select-" + suf);
        if (fontSelect) {
          fontSelect.addEventListener("change", (e) => {
            if (e.target.value) this.updateFont(e.target.value);
          });
        }
        const textColorVariant = document.querySelector(
          "#text-color-variant-" + suf
        );
        if (textColorVariant) {
          textColorVariant.addEventListener("change", (e) => {
            if (e.target.value) this.updateFontColor(e.target.value);
          });
        }
      });
      const handleSelection = (e) => {
        const tools = document.getElementById("text-align-tools");
        const onlyOne = e.selected && e.selected.length === 1;
        const isText =
          onlyOne &&
          (e.selected[0].type === "text" || e.selected[0].type === "textbox");
        if (tools) tools.style.display = isText ? "flex" : "none";

        // New: Scroll to corresponding sidebar variant
        if (onlyOne) {
          const obj = e.selected[0];
          const suf = state.currentBoard === 1 ? 'b1' : 'b2';
          let targetId;
          if (obj === this.backgroundImage) {
            targetId = `image-variant-${suf}`;
          } else if (obj === this.text) {
            targetId = `text-input-variant-${suf}`;
          }
          if (targetId) {
            const targetEl = document.getElementById(targetId);
            if (targetEl) {
              const scroller = findScrollableParent(targetEl);
              if (scroller) {
                const rectTarget = targetEl.getBoundingClientRect();
                const rectScroller = scroller.getBoundingClientRect();
                const offset = rectTarget.top - rectScroller.top;
                const centering = (scroller.clientHeight / 2) - (rectTarget.height / 2);
                const scrollToPos = scroller.scrollTop + offset - centering;
                scroller.scrollTo({ top: scrollToPos, behavior: 'smooth' });
              } else {
                targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          }
        }
      };
      this.canvas.on("selection:created", handleSelection);
      this.canvas.on("selection:updated", handleSelection);
      this.canvas.on("selection:cleared", () => {
        const textAlignTools = document.getElementById("text-align-tools");
        if (textAlignTools) textAlignTools.style.display = "none";
      });
      this.canvas.on("object:modified", () => {
        // persist the current board’s object transforms
        if (typeof saveCurrentBoardState === "function") {
          saveCurrentBoardState();
        }
      });
      this.canvas.on("object:moving", (e) => {
        if (!this.snappingEnabled || !this.clipPath) return;

        const obj = e.target;
        if (!obj) return;

        const threshold = 10;
        const cur = obj.getCenterPoint();
        const clipBR = this.clipPath.getBoundingRect(true);
        const clipCX = clipBR.left + clipBR.width / 2;
        const clipCY = clipBR.top + clipBR.height / 2;

        let targetCX = cur.x;
        let targetCY = cur.y;

        if (Math.abs(cur.x - clipCX) <= threshold) targetCX = clipCX;
        if (Math.abs(cur.y - clipCY) <= threshold) targetCY = clipCY;

        obj.setPositionByOrigin(
          new fabric.Point(targetCX, targetCY),
          "center",
          "center"
        );
        obj.setCoords();

        this.canvas.requestRenderAll();
      });

      this.canvas.on("object:added", (e) => {
        if (this.guidelineObjects.length) this._bringGuidesToFront();
      });
    }

    setupCustomControls() {
      const preload = (src) =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = src;
        });

      Promise.all([
        preload(this.icons.resizeLeft),
        preload(this.icons.resizeRight),
        preload(this.icons.resizeHorizontal),
        preload(this.icons.stackUp),
        preload(this.icons.stackDown),
        preload(this.icons.rotate),
      ])
        .then(
          ([
            resizeLeftImg,
            resizeRightImg,
            resizeHImg,
            stackUpImg,
            stackDownImg,
            rotateImg,
          ]) => {
            this.applyCustomCornerIcons(
              resizeLeftImg,
              resizeRightImg,
              resizeHImg,
              stackUpImg,
              stackDownImg,
              rotateImg
            );
          }
        )
        .catch((e) => console.warn("Icon preload failed:", e));

      fabric.Object.prototype.transparentCorners = false;
      fabric.Object.prototype.cornerColor = "black";
      fabric.Object.prototype.borderColor = "black";
      fabric.Object.prototype.cornerStyle = "circle";
      fabric.Object.prototype.cornerStrokeColor = "black";
      fabric.Object.prototype.borderScaleFactor = 1;
    }

    _topCeilingIndex() {
      if (!this.canvas) return -1;
      const objs = this.canvas.getObjects();
      let minGuide = Infinity;
      this.guidelineObjects.forEach((g) => {
        const idx = objs.indexOf(g);
        if (idx !== -1) minGuide = Math.min(minGuide, idx);
      });
      return minGuide === Infinity ? objs.length - 1 : minGuide - 1;
    }

    _bringGuidesToFront() {
      if (!this.canvas) return;
      this.guidelineObjects.forEach((g) => this.canvas.bringToFront(g));
      this.canvas.requestRenderAll();
    }

    _bgTopIndex() {
      if (!this.canvas) return -1;
      const objs = this.canvas.getObjects();
      let idx = -1;
      if (this.backgroundRect)
        idx = Math.max(idx, objs.indexOf(this.backgroundRect));
      if (this.backgroundImage)
        idx = Math.max(idx, objs.indexOf(this.backgroundImage));
      return idx;
    }

    _guardZ(obj) {
      if (!this.canvas || !obj) return;

      if (this.guidelineObjects?.length) this._bringGuidesToFront();

      const objs = this.canvas.getObjects();
      const bgTop = this._bgTopIndex();
      const topCap = this._topCeilingIndex();

      let cur = objs.indexOf(obj);
      if (bgTop >= 0 && cur <= bgTop) this.canvas.moveTo(obj, bgTop + 1);

      cur = objs.indexOf(obj);
      if (topCap >= 0 && cur > topCap) this.canvas.moveTo(obj, topCap);

      this.canvas.requestRenderAll();
    }

    ppiX() {
      if (!this.clipPath) return 1;
      const { width, height } = this.getBoardSize(); // in inches
      return this.clipPath.width / width; // px per inch (X)
    }
    ppiY() {
      if (!this.clipPath) return 1;
      const { width, height } = this.getBoardSize();
      return this.clipPath.height / height; // px per inch (Y)
    }

    // NEW: create a rectangular clip with a *hole* made by an inverted circle
    _buildBoardClipPath({ left, top, widthPx, heightPx }) {
      const { width: wIn, height: hIn } = this.getBoardSize(); // 24x48 in
      const sx = widthPx / wIn;
      const sy = heightPx / hIn;

      // Base rectangle (visible area)
      const rectClip = new fabric.Rect({
        left,
        top,
        width: widthPx,
        height: heightPx,
        absolutePositioned: true,
      });

      // Circular "punch-out" (inverted to subtract from the rectangle)
      const HOLE_DIAM_IN = 6;
      const HOLE_R_IN = HOLE_DIAM_IN / 2; // 3 in
      const HOLE_CX = left + widthPx / 2;
      const HOLE_CY = top + 12 * sy; // 12 in from the top

      const hole = new fabric.Circle({
        left: HOLE_CX,
        top: HOLE_CY,
        radius: HOLE_R_IN * sx,
        originX: "center",
        originY: "center",
        absolutePositioned: true,
      });

      // ✨ Subtract the circle from the rect by nesting it as an inverted clipPath
      hole.inverted = true;
      rectClip.clipPath = hole;

      return { rectClip, hole };
    }

    applyCustomCornerIcons(
      iconLeft,
      iconRight,
      iconHorizontal,
      iconStackUp,
      iconStackDown,
      iconRotate
    ) {
      const renderIcon = (icon) =>
        function (ctx, left, top, _style, fabricObject) {
          const size = this.cornerSize || 24;
          ctx.save();
          ctx.translate(left, top);
          ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle || 0));
          ctx.drawImage(icon, -size / 2, -size / 2, size, size);
          ctx.restore();
        };

      const setCtrl = (key, opts) => {
        const ctrl = fabric.Object.prototype.controls[key];
        if (ctrl) Object.assign(ctrl, opts);
      };

      const cornerSize = 25;
      setCtrl("tl", { render: renderIcon(iconLeft), cornerSize });
      setCtrl("tr", { render: renderIcon(iconRight), cornerSize });
      setCtrl("bl", { render: renderIcon(iconRight), cornerSize });
      setCtrl("br", { render: renderIcon(iconLeft), cornerSize });

      const midSize = 25;
      setCtrl("ml", {
        render: renderIcon(iconHorizontal),
        cornerSize: midSize,
      });
      setCtrl("mr", {
        render: renderIcon(iconHorizontal),
        cornerSize: midSize,
      });

      fabric.Object.prototype.setControlsVisibility({
        mt: false,
        mb: false,
        ml: false,
        mr: false,
      });

      const SIDE_OFFSET = 30;
      const self = this;

      const ICON_SIZE = 25;
      const OFFSET_X = 25;
      const OFFSET_Y = 12;
      const GAP_Y = 6;

      const makeTopRightControl = (name, order, iconImg, onClick) => {
        fabric.Object.prototype.controls[name] = new fabric.Control({
          x: 0.5,
          y: -0.5,
          offsetX: OFFSET_X,
          offsetY: OFFSET_Y + order * (ICON_SIZE + GAP_Y),
          cornerSize: ICON_SIZE,
          cursorStyle: "pointer",
          render: renderIcon(iconImg),
          mouseUpHandler: (_evt, transform) => {
            const obj = transform.target;
            if (!obj || !obj.canvas) return;
            onClick(obj);
            self._guardZ(obj);
            obj.canvas.setActiveObject(obj);
            obj.canvas.requestRenderAll();
          },
        });
      };

      makeTopRightControl("bringFwd", 0, iconStackUp, (obj) => {
        const cur = obj.canvas.getObjects().indexOf(obj);
        const maxIdx = self._topCeilingIndex();
        if (cur < maxIdx) obj.canvas.bringForward(obj);
      });

      makeTopRightControl("sendBack", 1, iconStackDown, (obj) => {
        const cur = obj.canvas.getObjects().indexOf(obj);
        const minIdx = self._bgTopIndex() + 1;
        if (cur > minIdx) obj.canvas.sendBackwards(obj);
      });

      const ROTATE_SIZE = 26;
      const ROTATE_OFFSETY = -32;

      fabric.Object.prototype.controls.mtr = new fabric.Control({
        x: 0,
        y: -0.5,
        offsetY: ROTATE_OFFSETY,
        cornerSize: ROTATE_SIZE,
        cursorStyleHandler: fabric.controlsUtils.rotationStyleHandler,
        actionHandler: fabric.controlsUtils.rotationWithSnapping,
        actionName: "rotate",
        render: renderIcon(iconRotate),
        withConnection: true,
        connectionStyle: { stroke: "#000", strokeWidth: 1 },
      });
    }

    handleResize() {
      if (!this.canvas) return;
      this.updateCanvasSize();
      this.drawClipPath();
      this.canvas.renderAll();
    }

    updateCanvasSize() {
      const container = document.getElementById("canvasContainer");
      if (!container || !this.canvas) return;
      if (container.clientWidth === 0 && container.clientHeight === 0) return;

      const width = Math.max(1, container.clientWidth || 1);
      const small = window.matchMedia("(max-width: 640px)").matches;

      const ratio = 24 / 48; // width / height

      let height;

      if (small) {
        height = Math.max(120, Math.round(width / ratio));
        const nearSquare = ratio >= 0.9 && ratio <= 1.1;
        const midSquare = ratio > 0.75 && ratio < 1.33;
        const shrink = nearSquare ? 0.75 : midSquare ? 0.85 : 0.9; // Adjusted shrink factors
        height = Math.round(height * shrink);
        const maxVH = Math.floor(window.innerHeight * 0.4); // Reduced from 0.6 to 0.5
        height = Math.min(height, maxVH);
      } else {
        height = Math.max(
          1,
          container.clientHeight || Math.round(width / ratio)
        );
      }

      this.canvas.setDimensions({ width, height });
      this.canvas.calcOffset();
      this.canvas.renderAll();
    }

    // Fixed size for cornhole board
    getBoardSize() {
      return { width: 24, height: 48 }; // in inches
    }

    mobileFillForRatio(ratio) {
      if (ratio >= 0.9 && ratio <= 1.1) return 0.88;
      if (ratio >= 1.6 || ratio <= 0.625) return 0.95;
      return 0.9;
    }

    // New method to check if any content is present for current board
    hasContent() {
      const boardState = state[`board${state.currentBoard}`];
      return (
        (boardState.color && boardState.color !== "None") ||
        !!boardState.imageUrl ||
        !!(boardState.text && boardState.text.trim())
      );
    }

    // New method to update the finishEditing button state
    updateFinishButtonState() {
      const finishEditing = document.getElementById("finishEditing");
      if (finishEditing) {
        finishEditing.disabled = !this.hasContent();
      }
    }

    _snapshotObj(obj) {
      if (!obj || !this.clipPath) return null;
      const b = this.clipPath.getBoundingRect(true);
      return {
        // center-relative location in the clipped area
        cx: (obj.left - b.left) / b.width,
        cy: (obj.top - b.top) / b.height,
        // keep their transforms
        scaleX: obj.scaleX,
        scaleY: obj.scaleY,
        angle: obj.angle || 0,
        originX: obj.originX || "center",
        originY: obj.originY || "center",
        // capture clip size so we can scale across resizes
        clipW: b.width,
        clipH: b.height,
        // Store textAlign for text objects
        textAlign:
          obj.type === "text" || obj.type === "textbox"
            ? obj.textAlign || "left"
            : undefined,
      };
    }

    _applySnapshot(obj, snap) {
      if (!obj || !snap || !this.clipPath) return;
      const b = this.clipPath.getBoundingRect(true);
      const kx = b.width / (snap.clipW || b.width);
      const ky = b.height / (snap.clipH || b.height);

      obj.set({
        left: b.left + snap.cx * b.width,
        top: b.top + snap.cy * b.height,
        originX: snap.originX,
        originY: snap.originY,
        scaleX: (snap.scaleX || 1) * kx,
        scaleY: (snap.scaleY || 1) * ky,
        angle: snap.angle || 0,
        // Apply textAlign if it exists
        ...(snap.textAlign && (obj.type === "text" || obj.type === "textbox")
          ? { textAlign: snap.textAlign }
          : {}),
      });
      obj.setCoords();
    }

    drawClipPath() {
      if (!this.canvas) return;
      // If frozen but we DON'T have clip/background yet, we must build them.
      if (this.freezeLayout && this.clipPath && this.backgroundRect) return;

      const container = document.getElementById("canvasContainer");
      if (!container) return;

      // 🧹 if container isn't laid out yet, still clear so old content doesn't linger
      if (container.clientWidth === 0 || container.clientHeight === 0) {
        this.canvas.clear();
        this.canvas.clipPath = null;
        this.canvas.renderAll();
        return;
      }

      // Preserve objects we want to keep
      const keep = [this.backgroundImage, this.text].filter(Boolean);

      // Remember old clip metrics to reposition content after resize
      let old = null;
      if (this.clipPath) {
        old = {
          left: this.clipPath.left,
          top: this.clipPath.top,
          w: this.clipPath.width,
          h: this.clipPath.height,
        };
      }

      // Reset canvas
      this.canvas.clear();
      this.canvas.clipPath = null;

      // Board aspect & target size inside container
      const { width: wIn, height: hIn } = this.getBoardSize(); // 24x48
      const ratio = wIn / hIn;
      const small = window.matchMedia("(max-width: 640px)").matches;
      const fill = small ? this.mobileFillForRatio(ratio) : 0.8;

      const maxW = container.clientWidth * fill;
      const maxH = container.clientHeight * fill;

      const widthPx = ratio > maxW / maxH ? maxW : maxH * ratio;
      const heightPx = ratio > maxW / maxH ? maxW / ratio : maxH;

      const centerX = this.canvas.getWidth() / 2;
      const centerY = this.canvas.getHeight() / 2;
      const left = centerX - widthPx / 2;
      const top = centerY - heightPx / 2;

      // Background rectangle that receives color (and sits under image/text)
      if (!this.backgroundRect) {
        this.backgroundRect = new fabric.Rect({
          left,
          top,
          width: widthPx,
          height: heightPx,
          fill: "transparent",
          selectable: false,
          evented: false,
        });
        this.canvas.add(this.backgroundRect);
      } else {
        this.backgroundRect.set({
          left,
          top,
          width: widthPx,
          height: heightPx,
        });
        this.canvas.add(this.backgroundRect);
      }

      // Create board clip (rect − circle) and assign to canvas
      const { rectClip } = this._buildBoardClipPath({
        left,
        top,
        widthPx,
        heightPx,
      });
      this.clipPath = rectClip;
      this.canvas.clipPath = this.clipPath;

      // Re-add existing content, keep relative position through resizes
      const scale = old ? widthPx / old.w : 1;
      keep.forEach((obj) => {
        this.canvas.add(obj);
        if (old) {
          const relX = (obj.left - old.left) / old.w;
          const relY = (obj.top - old.top) / old.h;
          obj.set({
            left: left + relX * widthPx,
            top: top + relY * heightPx,
            scaleX: obj.scaleX * scale,
            scaleY: obj.scaleY * scale,
          });
        }
        obj.setCoords();
      });

      // Set current color on backgroundRect
      const boardState = state[`board${state.currentBoard}`];
      this.backgroundRect.set({
        fill: COLOR_MAPPINGS[boardState.color] || "transparent",
      });

      // ✨ Clean guidelines (single dashed rectangle + dashed ring for the hole)
      this._drawGuides();

      // Z-order
      if (this.backgroundImage) this.canvas.sendToBack(this.backgroundImage);
      if (this.backgroundRect) this.canvas.sendToBack(this.backgroundRect);

      this.canvas.renderAll();
      this.updateFinishButtonState();
    }

    _drawGuides() {
      // remove old
      this.guidelineObjects.forEach((o) => this.canvas.remove(o));
      this.guidelineObjects = [];

      const show = document.getElementById("toggleGuides")?.checked || false;
      if (!show || !this.clipPath) return;

      const left = this.clipPath.left;
      const top = this.clipPath.top;
      const wPx = this.clipPath.width;
      const hPx = this.clipPath.height;
      const sx = this.ppiX();
      const sy = this.ppiY();

      // 1in inset safe area
      const inset = 0;
      const rectGuide = new fabric.Rect({
        left: left + inset * sx,
        top: top + inset * sy,
        width: wPx - 2 * inset * sx,
        height: hPx - 2 * inset * sy,
        fill: "transparent",
        stroke: "#ec008c",
        strokeWidth: 1,
        strokeDashArray: [6, 3],
        selectable: false,
        evented: false,
        strokeUniform: true,
      });

      // Dashed ring at the hole
      const HOLE_R_IN = 3;
      const holeCx = left + wPx / 2;
      const holeCy = top + 12 * sy;
      const ring = new fabric.Circle({
        left: holeCx,
        top: holeCy,
        originX: "center",
        originY: "center",
        radius: HOLE_R_IN * sx,
        fill: "transparent",
        stroke: "#ec008c",
        strokeWidth: 1,
        strokeDashArray: [6, 3],
        selectable: false,
        evented: false,
        strokeUniform: true,
      });

      this.canvas.add(rectGuide);
      this.canvas.add(ring);
      this.guidelineObjects.push(rectGuide, ring);
    }

    async renderBoardFromState(boardIndex) {
      const bs = state[`board${boardIndex}`] || {};
      const token = ++this._renderToken;

      // Fully reset the canvas to avoid residual content
      this.canvas.clear();
      this.canvas.clipPath = null;
      this.backgroundRect = null;
      this.backgroundImage = null;
      this.text = null;
      this.guidelineObjects = [];

      // Rebuild the clip path and background
      this.drawClipPath();

      // Set background color
      if(this.backgroundRect){
        this.backgroundRect.set({
          fill: COLOR_MAPPINGS[bs.color] || "transparent",
        });
      }

      // Load font before applying text
      const fontFamily =
        bs.font === "3rd Rail"
          ? "Third_Rail"
          : (bs.font || "Anton").replaceAll(" ", "");
      if (bs.text && bs.text.trim()) {
        await this.loadFont(fontFamily);
      }

      // Apply text
      this.updateFont(bs.font || "Anton");
      this.updateFontColor(bs.fontColor || "Black");
      this.updateText(bs.text || "");

      if (this.text && bs.textTransform) {
        this._applySnapshot(this.text, bs.textTransform);
      }

      // Apply image if it exists and belongs to the current board
      if (bs.imageUrl && boardIndex === state.currentBoard) {
        await new Promise((resolve) => {
          fabric.Image.fromURL(
            bs.imageUrl,
            (img) => {
              if (token !== this._renderToken) return resolve();
              this._placeImage(img, bs.imageTransform || null);
              resolve();
            },
            { crossOrigin: "anonymous" }
          );
        });
      }

      this.canvas.renderAll();
      this.updateFinishButtonState();
    }

    _exportBoardPng(multiplier = 2) {
      const { left, top, width, height } = this.clipPath;
      return this.canvas.toDataURL({
        left,
        top,
        width,
        height,
        format: "png",
        multiplier,
      });
    }

    toggleGuidelines(visible) {
      if (!this.canvas) return;
      this.guidelineObjects.forEach((obj) => (obj.visible = visible));
      this.canvas.renderAll();
    }

    alignObjects(direction) {
      if (!this.canvas || !this.clipPath) return;
      const activeObjects = this.canvas.getActiveObjects();
      if (!activeObjects.length) return;
      if (activeObjects.length === 1) {
        const bounds = this.clipPath.getBoundingRect(true);
        activeObjects.forEach((obj) => {
          switch (direction) {
            case "left":
              obj.set({ left: bounds.left + obj.getScaledWidth() / 2 });
              break;
            case "centerH":
              obj.set({ left: bounds.left + bounds.width / 2 });
              break;
            case "right":
              obj.set({
                left: bounds.left + bounds.width - obj.getScaledWidth() / 2,
              });
              break;
            case "top":
              obj.set({ top: bounds.top + obj.getScaledHeight() / 2 });
              break;
            case "centerV":
              obj.set({ top: bounds.top + bounds.height / 2 });
              break;
            case "bottom":
              obj.set({
                top: bounds.top + bounds.height - obj.getScaledHeight() / 2,
              });
              break;
          }
          obj.setCoords();
        });
      } else {
        let minLeft = Infinity,
          maxRight = -Infinity,
          minTop = Infinity,
          maxBottom = -Infinity;
        let totalCenterX = 0,
          totalCenterY = 0;
        activeObjects.forEach((obj) => {
          const bound = obj.getBoundingRect(true);
          minLeft = Math.min(minLeft, bound.left);
          maxRight = Math.max(maxRight, bound.left + bound.width);
          minTop = Math.min(minTop, bound.top);
          maxBottom = Math.max(maxBottom, bound.top + bound.height);
          totalCenterX += bound.left + bound.width / 2;
          totalCenterY += bound.top + bound.height / 2;
        });
        const avgCenterX = totalCenterX / activeObjects.length;
        const avgCenterY = totalCenterY / activeObjects.length;
        activeObjects.forEach((obj) => {
          switch (direction) {
            case "left":
              obj.set({ left: minLeft + obj.getScaledWidth() / 2 });
              break;
            case "centerH":
              obj.set({ left: avgCenterX });
              break;
            case "right":
              obj.set({ left: maxRight - obj.getScaledWidth() / 2 });
              break;
            case "top":
              obj.set({ top: minTop + obj.getScaledHeight() / 2 });
              break;
            case "centerV":
              obj.set({ top: avgCenterY });
              break;
            case "bottom":
              obj.set({ top: maxBottom - obj.getScaledHeight() / 2 });
              break;
          }
          obj.setCoords();
        });
      }
      this.canvas.renderAll();
    }

    updateBackgroundColor(color) {
      const boardState = state[`board${state.currentBoard}`];
      boardState.color = color;
      if (this.backgroundRect) {
        this.backgroundRect.set({
          fill: COLOR_MAPPINGS[color] || "transparent",
        });
        this.canvas.renderAll();
      } else {
        this.drawClipPath();
      }
      this.updateFinishButtonState();
    }

    _placeImage(img, snap = null) {
      const centerX = this.canvas.getWidth() / 2;
      const centerY = this.canvas.getHeight() / 2;
      const clipW = this.clipPath.width;
      const clipH = this.clipPath.height;

      // Default: cover the board (first time)
      if (!snap) {
        const scale = Math.max(clipW / img.width, clipH / img.height);
        img.set({
          scaleX: scale,
          scaleY: scale,
          originX: "center",
          originY: "center",
          left: centerX,
          top: centerY,
        });
      } else {
        // Rehydrate exactly as saved
        img.set({ originX: "center", originY: "center" });
      }

      img.set({
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true,
      });
      img.setCoords();

      if (this.backgroundImage) this.canvas.remove(this.backgroundImage);
      this.canvas.add(img);
      this.backgroundImage = img;

      if (snap) this._applySnapshot(img, snap);

      this.canvas.sendToBack(this.backgroundImage);
      this.canvas.sendBackwards(this.backgroundRect);
      this.canvas.renderAll();
      this.updateFinishButtonState();
    }

    handleImageUpload(event, explicitBoardIdx = null) {
      const file = event.target.files?.[0];
      if (!file) return;

      const targetBoard = explicitBoardIdx ?? state.currentBoard;

      // Ensure clip is ready if we’re staying on this board
      if (!this.canvas || !this.clipPath) this.drawClipPath();

      const reader = new FileReader();
      reader.onload = (e) => {
        const url = e.target.result;

        // Always write to the originating board’s state
        const bs = state[`board${targetBoard}`];
        bs.imageUrl = url;
        bs.image = file;

        // If user is STILL on that board, place it on the canvas now
        if (state.currentBoard === targetBoard) {
          const renderTokenAtUpload = this._renderToken; // (see section B)
          fabric.Image.fromURL(
            url,
            (img) => {
              // If a newer render started since we queued this, ignore
              if (renderTokenAtUpload !== this._renderToken) return;

              this._placeImage(img);
              // snapshot after first placement
              bs.imageTransform = this._snapshotObj(this.backgroundImage);
              this.canvas.renderAll();
            },
            { crossOrigin: "anonymous" }
          );
        }
        // If the user switched boards, we do NOT touch the current canvas here.
        // When they come back, renderBoardFromState() will hydrate from bs.imageUrl.
      };
      reader.readAsDataURL(file);
    }

    async loadFont(fontFamily) {
      // Check if font is already loaded
      if (document.fonts.check(`12px "${fontFamily}"`)) {
        return;
      }

      // Load the font
      try {
        await document.fonts.load(`12px "${fontFamily}"`);
      } catch (err) {
        console.error(`Failed to load font ${fontFamily}:`, err);
      }
    }

    updateText(text) {
      const boardState = state[`board${state.currentBoard}`];
      boardState.text = text;

      if (!this.canvas || !this.clipPath) {
        this.drawClipPath();
      }

      // Remove existing text object to prevent overlap
      if (this.text) {
        this.canvas.remove(this.text);
        this.text = null;
      }

      if (!text.trim()) {
        this.canvas.renderAll();
        this.updateFinishButtonState();
        return;
      }

      const currentFont = boardState.font.replaceAll(" ", "") || "Anton";
      const currentColor = FONT_COLORS[boardState.fontColor] || "Black";
      // Use saved textAlign if it exists, otherwise default to 'left'
      const currentTextAlign = boardState.textTransform?.textAlign || "left";

      const textObj = new fabric.Text(text, {
        fontFamily: currentFont === "3rd Rail" ? "Third_Rail" : currentFont,
        fill: currentColor,
        originX: "center",
        originY: "center",
        left: this.canvas.getWidth() / 2,
        top: this.canvas.getHeight() / 2,
        fontSize: 40,
        lineHeight: 1,
        selectable: true,
        evented: true,
        textBaseline: "alphabetic",
        editable: false,
        textAlign: currentTextAlign,
      });

      const baseWidth = textObj.width;
      const limit = this.clipPath.width * 0.8;
      let newScale = limit / baseWidth;
      newScale = Math.min(1, newScale);
      textObj.set({ scaleX: newScale, scaleY: newScale });
      textObj.setCoords();

      this.canvas.add(textObj);
      this.text = textObj;
      this.text.initDimensions(); // Reinitialize dimensions to apply font correctly
      this.canvas.bringToFront(this.text);
      this.canvas.setActiveObject(this.text);
      this.canvas.renderAll();
      this.updateFinishButtonState();
    }

    updateFont(font) {
      if (!font) {
        console.warn("Font is undefined, using default Anton");
        font = "Anton";
      }
      const boardState = state[`board${state.currentBoard}`];
      boardState.font = font;
      if (this.text) {
        const fontFamily =
          font === "3rd Rail" ? "Third_Rail" : font.replaceAll(" ", "");
        this.text.set("fontFamily", fontFamily);
        this.text.initDimensions(); // Reinitialize dimensions after changing font
        this.canvas.renderAll();
      }
    }

    updateFontColor(color) {
      if (!color) return;
      const boardState = state[`board${state.currentBoard}`];
      boardState.fontColor = color;
      if (this.text) {
        this.text.set("fill", FONT_COLORS[color]);
        this.canvas.renderAll();
      }
    }
    
    async finishEditing() {
    // Store current board to restore later
    const originalBoard = state.currentBoard;
    try {
        // Ensure we have a canvas & clip; build if needed
        if (!this.canvas || !this.clipPath) this.drawClipPath();

        // ---------- Board 1 (always) ----------
        state.currentBoard = 1;
        await this.renderBoardFromState(1);
        // Wait for rendering and font application
        await new Promise((resolve) => setTimeout(resolve, 300));
        this.canvas.renderAll();
        // Hide guidelines before export
        this.guidelineObjects.forEach((g) => (g.visible = false));
        this.canvas.renderAll();
        const dataURL1 = this._exportBoardPng();
        state.editedBoard1 = dataURLtoFile(dataURL1, "board1.png");
        // Update preview for Board 1
        const previewImage1 = document.getElementById("preview-image1");
        if (previewImage1) previewImage1.src = dataURL1;

        // ---------- Board 2 (only if customized) ----------
        const previewImage2 = document.getElementById("preview-image2");
        const preview2Wrap = document.querySelector(".board-preview2");

        if (state.customizeBoard2 && isBoardCustomized(state.board2)) {
        state.currentBoard = 2;
        await this.renderBoardFromState(2);
        // Wait for rendering and font application
        await new Promise((resolve) => setTimeout(resolve, 300));
        this.canvas.renderAll();
        // Hide guidelines before export
        this.guidelineObjects.forEach((g) => (g.visible = false));
        this.canvas.renderAll();
        const dataURL2 = this._exportBoardPng();
        state.editedBoard2 = dataURLtoFile(dataURL2, "board2.png");
        if (previewImage2) previewImage2.src = dataURL2;
        if (preview2Wrap) preview2Wrap.style.display = "block";
        } else {
        state.editedBoard2 = null;
        if (previewImage2) previewImage2.src = "";
        if (preview2Wrap) preview2Wrap.style.display = "none";
        }

        // Restore original board
        state.currentBoard = originalBoard;
        await this.renderBoardFromState(originalBoard);

        // UI updates
        const designerTool = document.getElementById("designerTool");
        const previewBox = document.getElementById("preview-box");
        const mainVariants = document.getElementById("main-variants");
        const uploadArtbox = document.getElementById("upload-artbox");
        if (designerTool) designerTool.classList.remove("active");
        enableBodyScroll()
        if (previewBox) previewBox.style.display = "block";
        if (mainVariants) mainVariants.classList.add("active");
        if (uploadArtbox) uploadArtbox.style.display = "none";
    } catch (err) {
        console.error("Error saving edited image:", err);
        this.showError("Failed to save edited image. Please try again.");
    } finally {
        // Restore original board state
        state.currentBoard = originalBoard;
        // Restore guide visibility after all exports
        const toggleGuides = document.getElementById("toggleGuides");
        const shouldShowGuides = toggleGuides ? toggleGuides.checked : false;
        if (this.guidelineObjects.length > 0) {
        this.guidelineObjects.forEach((g) => (g.visible = shouldShowGuides));
        this.canvas.renderAll();
        }
    }
    }

    handleRemoveAllImproved() {
      // Clear canvas but don't dispose it
      if (this.canvas) {
        this.canvas.clear();
        this.canvas.clipPath = null;

        // Reset internal references
        this.clipPath = null;
        this.backgroundRect = null;
        this.backgroundImage = null;
        this.text = null;
        this.guidelineObjects = [];

        // Reinitialize the canvas properly
        this.drawClipPath();

        // Reset to default state for current board
        const boardState = state[`board${state.currentBoard}`];
        this.updateBackgroundColor(boardState.color || "None");
        this.updateText(boardState.text || "");
        this.updateFont(boardState.font || "Anton");
        this.updateFontColor(boardState.fontColor || "Black");
      }
    }

    handleRemoveAll() {
      // Dispose of the current canvas
      if (this.canvas) {
        this.canvas.clear();
        this.canvas.clipPath = null;
        this.canvas.dispose();
        this.canvas = null;
      }

      // Clear all internal references
      this.clipPath = null;
      this.backgroundRect = null;
      this.backgroundImage = null;
      this.text = null;
      this.guidelineObjects = [];
      this._ro?.disconnect(); // Disconnect ResizeObserver

      // Reset state
      state.board1.image = null;
      state.board1.imageUrl = null;
      state.board1.text = "";
      state.board1.color = "None";
      state.board1.imageTransform = null;
      state.board1.textTransform = null;
      state.board2.image = null;
      state.board2.imageUrl = null;
      state.board2.text = "";
      state.board2.color = "None";
      state.board2.imageTransform = null;
      state.board2.textTransform = null;
      state.editedBoard1 = null;
      state.editedBoard2 = null;
      state.customizeBoard2 = false;
      state.extraForBoard2 = 0;

      // Reset UI elements
      ["b1", "b2"].forEach((suf) => {
        const textInput = document.getElementById("text-input-" + suf);
        if (textInput) textInput.value = "";
      });

      const designerTool = document.getElementById("designerTool");
      if (designerTool) {
        designerTool.classList.remove("active");
        enableBodyScroll()
      }

      const mainVariants = document.getElementById("main-variants");
      if (mainVariants) mainVariants.classList.remove("active");

      const previewBox = document.getElementById("preview-box");
      if (previewBox) previewBox.style.display = "none";

      const uploadArtbox = document.getElementById("upload-artbox");
      if (uploadArtbox) uploadArtbox.style.display = "";

      // Reinitialize the designer
      this.init();

      // Ensure canvas is empty after initialization
      if (this.canvas) {
        this.canvas.clear();
        this.canvas.clipPath = null;
        this.canvas.backgroundColor = "white";
        this.canvas.renderAll();
      }

      this.updateFinishButtonState();
    }

    showError(message) {
      const msgEl = document.getElementById("canvasMessage");
      if (msgEl) {
        msgEl.classList.remove("warning");
        msgEl.classList.add("error");
        msgEl.textContent = message;
        show(msgEl);
        setTimeout(() => hide(msgEl), 5000);
      }
    }

    showWarning(message) {
      const msgEl = document.getElementById("canvasMessage");
      if (msgEl) {
        msgEl.classList.remove("error");
        msgEl.classList.add("warning");
        msgEl.textContent = message;
        show(msgEl);
        setTimeout(() => hide(msgEl), 5000);
      }
    }
  }
  
  // =============================================
  // 18. INITIALIZE EVERYTHING
  // =============================================
  function initialize() {
    // Dynamically add the snapping toggle to inline-actions (reuse guides styles)
    const inlineActions = document.querySelector(".inline-actions");
    if (inlineActions && !document.getElementById("toggleSnap")) {
      const snapToggle = document.createElement("label");
      snapToggle.className = "guides-toggle-switch pccb-toggle";
      snapToggle.innerHTML = `
        <input type="checkbox" id="toggleSnap" checked />
        <span class="guides-slider"></span>
        <span class="guides-label-text">Enable Snapping</span>
      `;
      inlineActions.appendChild(snapToggle);
    }
    designer = new CornholeDesigner();
    bindUploadForBoard(1);
    bindUploadForBoard(2);
    initDropdownDefaults();
    [1, 2].forEach((board) => {
      setupTextColorSelection(board);
      handleTextInput(board);
    });
    handleNotesInput();
    setupProtectionSelection();
    setupApippaAddToCart();
    setupBoardSwitching();
    document.addEventListener("click", dropdownClickHandler);
    document.getElementById("image-variant-b2").style.display =
      state.customizeBoard2 ? "" : "none";
    document.getElementById("variants-b2").style.display = state.customizeBoard2
      ? ""
      : "none";
    document.getElementById("image-variant-b1").style.display = "";
    document.getElementById("variants-b1").style.display = "";
    recalcPrice();
    loadCurrentBoardState();
  }
  initialize();
});
